import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
];

const LanguageContext = createContext();

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => localStorage.getItem('fos_language') || 'en');
  const [translations, setTranslations] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`fos_trans_${localStorage.getItem('fos_language') || 'en'}`) || '{}'); }
    catch { return {}; }
  });
  const [loading, setLoading] = useState(false);
  const pendingStrings = useRef(new Set());
  const batchTimer = useRef(null);
  const observerRef = useRef(null);
  const translatingRef = useRef(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  // Load user preference from DB on mount
  useEffect(() => {
    const loadPreference = async () => {
      try {
        const res = await axios.get(`${API}/user/language-preference`, getAuthHeaders());
        if (res.data.language && res.data.language !== language) {
          setLanguageState(res.data.language);
          localStorage.setItem('fos_language', res.data.language);
        }
      } catch { /* ignore */ }
    };
    const token = localStorage.getItem('authToken');
    if (token) loadPreference();
  }, []);

  // Load dictionary when language changes
  useEffect(() => {
    if (language === 'en') {
      setTranslations({});
      return;
    }
    const loadDictionary = async () => {
      try {
        const cached = localStorage.getItem(`fos_trans_${language}`);
        if (cached) {
          setTranslations(JSON.parse(cached));
        }
        const res = await axios.get(`${API}/translations/dictionary/${language}`);
        if (res.data.translations) {
          setTranslations(res.data.translations);
          localStorage.setItem(`fos_trans_${language}`, JSON.stringify(res.data.translations));
        }
      } catch { /* ignore */ }
    };
    loadDictionary();
  }, [language]);

  // Batch translate queued strings
  const flushPendingTranslations = useCallback(async () => {
    if (language === 'en' || pendingStrings.current.size === 0 || translatingRef.current) return;
    translatingRef.current = true;
    const strings = Array.from(pendingStrings.current).slice(0, 80);
    pendingStrings.current.clear();

    try {
      const res = await axios.post(`${API}/translations/batch-translate`, {
        strings, target_language: language
      });
      if (res.data.translations) {
        setTranslations(prev => {
          const merged = { ...prev, ...res.data.translations };
          localStorage.setItem(`fos_trans_${language}`, JSON.stringify(merged));
          return merged;
        });
      }
    } catch { /* ignore */ }
    finally { translatingRef.current = false; }
  }, [language]);

  // Queue a string for translation
  const queueForTranslation = useCallback((str) => {
    if (!str || language === 'en' || translations[str]) return;
    pendingStrings.current.add(str);
    if (batchTimer.current) clearTimeout(batchTimer.current);
    batchTimer.current = setTimeout(flushPendingTranslations, 1500);
  }, [language, translations, flushPendingTranslations]);

  // Translation function
  const t = useCallback((text) => {
    if (!text || typeof text !== 'string' || language === 'en') return text;
    const trimmed = text.trim();
    if (translations[trimmed]) return translations[trimmed];
    queueForTranslation(trimmed);
    return text;
  }, [language, translations, queueForTranslation]);

  // Change language
  const setLanguage = useCallback(async (newLang) => {
    setLoading(true);
    setLanguageState(newLang);
    localStorage.setItem('fos_language', newLang);

    // Save to DB
    try {
      await axios.post(`${API}/user/language-preference`, { language: newLang }, getAuthHeaders());
    } catch { /* ignore */ }

    if (newLang === 'en') {
      setTranslations({});
      setLoading(false);
      // Reload page to reset all DOM text to English
      window.location.reload();
      return;
    }

    // Collect ALL visible text from DOM + common strings
    const allStrings = new Set(getCommonUIStrings());
    const root = document.getElementById('root');
    if (root) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      while (walker.nextNode()) {
        const text = walker.currentNode.textContent?.trim();
        if (text && text.length > 1 && text.length < 200 && !/^[\d\s.,:%₹$\/\-+()]+$/.test(text)) {
          allStrings.add(text);
        }
      }
    }

    // Check what's already cached
    let cachedTrans = {};
    try {
      const cached = localStorage.getItem(`fos_trans_${newLang}`);
      if (cached) cachedTrans = JSON.parse(cached);
    } catch { /* ignore */ }

    const uncached = Array.from(allStrings).filter(s => !cachedTrans[s]);
    
    // Start with cached translations immediately
    if (Object.keys(cachedTrans).length > 0) {
      setTranslations(cachedTrans);
    }

    // Translate uncached strings if any
    if (uncached.length > 0) {
      try {
        const res = await axios.post(`${API}/translations/batch-translate`, {
          strings: uncached.slice(0, 150), target_language: newLang
        });
        if (res.data.translations) {
          const merged = { ...cachedTrans, ...res.data.translations };
          setTranslations(merged);
          localStorage.setItem(`fos_trans_${newLang}`, JSON.stringify(merged));
        }
      } catch { /* ignore */ }
    }
    
    setLoading(false);
  }, []);

  // ==================== DOM AUTO-TRANSLATION ====================
  
  const translateDOM = useCallback(() => {
    if (language === 'en' || Object.keys(translations).length === 0) return;
    
    const root = document.getElementById('root');
    if (!root) return;
    
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.dataset?.noTranslate !== undefined) return NodeFilter.FILTER_REJECT;
        if (['SCRIPT', 'STYLE', 'TEXTAREA'].includes(parent.tagName)) return NodeFilter.FILTER_REJECT;
        const text = node.textContent?.trim();
        if (!text || text.length < 2) return NodeFilter.FILTER_REJECT;
        if (/^[\d\s.,:%₹$\/\-+()]+$/.test(text)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    const newStringsToQueue = [];
    
    while (walker.nextNode()) {
      const node = walker.currentNode;
      const text = node.textContent.trim();
      
      // Store original text
      if (!node._origText) node._origText = text;
      const orig = node._origText;
      
      if (translations[orig]) {
        if (node.textContent.trim() !== translations[orig]) {
          node.textContent = node.textContent.replace(text, translations[orig]);
        }
      } else if (orig.length > 1 && orig.length < 200) {
        newStringsToQueue.push(orig);
      }
    }
    
    // Also translate placeholder attributes
    root.querySelectorAll('input[placeholder], textarea[placeholder]').forEach(el => {
      if (el.dataset?.noTranslate !== undefined) return;
      const orig = el.dataset.origPlaceholder || el.placeholder;
      if (!el.dataset.origPlaceholder && orig) el.dataset.origPlaceholder = orig;
      if (translations[orig]) el.placeholder = translations[orig];
      else if (orig && orig.length > 2) newStringsToQueue.push(orig);
    });

    // Queue any new untranslated strings
    newStringsToQueue.forEach(s => pendingStrings.current.add(s));
    if (newStringsToQueue.length > 0) {
      if (batchTimer.current) clearTimeout(batchTimer.current);
      batchTimer.current = setTimeout(flushPendingTranslations, 2000);
    }
  }, [language, translations, flushPendingTranslations]);

  // MutationObserver for auto-translation
  useEffect(() => {
    if (language === 'en') {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    // Initial translation pass
    const timer = setTimeout(translateDOM, 600);

    // Observe DOM changes  
    observerRef.current = new MutationObserver(() => {
      // Debounce translations on rapid mutations
      if (observerRef.current._debounce) clearTimeout(observerRef.current._debounce);
      observerRef.current._debounce = setTimeout(translateDOM, 300);
    });

    const rootEl = document.getElementById('root') || document.body;
    observerRef.current.observe(rootEl, { childList: true, subtree: true });

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        if (observerRef.current._debounce) clearTimeout(observerRef.current._debounce);
        observerRef.current.disconnect();
      }
    };
  }, [language, translateDOM]);

  // Re-run when translations dict updates
  useEffect(() => {
    if (language === 'en' || Object.keys(translations).length === 0) return;
    const timer = setTimeout(translateDOM, 200);
    return () => clearTimeout(timer);
  }, [translations, language, translateDOM]);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      loading,
      languages: SUPPORTED_LANGUAGES,
      translations
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Common UI strings to pre-seed translations
function getCommonUIStrings() {
  return [
    // Navigation
    'Dashboard', 'Home', 'Settings', 'Profile', 'Logout', 'Login', 'Sign In', 'Sign Out',
    'Save', 'Cancel', 'Delete', 'Edit', 'Add', 'Remove', 'Close', 'Back', 'Next', 'Submit',
    'Search', 'Filter', 'Refresh', 'Loading', 'No data found', 'View', 'Download',
    // Admin sections
    'Franchises', 'Menu Management', 'Raw Materials', 'RM Orders', 'Reports', 'Staff',
    'Attendance', 'Expenses', 'Payroll', 'Subscription', 'Onboarding', 'System Settings',
    'Ledger Management', 'Billing', 'Orders',
    // Franchise sections
    'Generate Order', 'Past Orders', 'Raw Material Order', 'My Orders', 'Place Order',
    'Sales', 'Today', 'This Week', 'This Month',
    // Status labels
    'Pending', 'Accepted', 'Rejected', 'Altered', 'Paid', 'Delivered', 'In Transit',
    'Active', 'Inactive', 'Completed',
    // Common labels
    'Total', 'Amount', 'Quantity', 'Price', 'Name', 'Date', 'Time', 'Status',
    'Phone', 'Email', 'Address', 'Description', 'Category', 'Type',
    'Order ID', 'Order Details', 'Payment Status', 'Delivery Status',
    'Grand Total', 'Items', 'Actions',
    // Messages
    'Are you sure?', 'This action cannot be undone', 'Successfully saved',
    'Failed to load', 'Something went wrong', 'Please try again',
    'No orders found', 'No items found',
    // RM Order specific
    'Accept', 'Reject', 'Alter', 'Rejection Reason', 'Activity Log',
    'Order placed successfully', 'Order Total', 'Additional Requirements',
    'Select Products', 'Clear All', 'Submit Order', 'Previous Orders',
    // Staff
    'Staff Management', 'Mark Attendance', 'Staff Name', 'Role', 'Salary',
    // Billing
    'Invoice', 'Bill', 'Receipt', 'Tax', 'Discount', 'Subtotal',
    'Cash', 'Credit', 'Payment Method', 'Customer Name', 'Customer Phone',
    // General
    'Select', 'All', 'None', 'Yes', 'No', 'OK', 'Confirm',
    'From', 'To', 'Start Date', 'End Date',
    'Total Orders', 'Total Revenue', 'Total Amount',
    'Franchise Name', 'Location', 'Contact Number',
    // Language
    'Language', 'Change Language', 'Select Language',
    'English', 'Hindi', 'Kannada', 'Telugu', 'Tamil', 'Malayalam',
  ];
}

export default LanguageContext;
